import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as api from "./adminApi";
import {
  AdminShell,
  Area,
  Btn,
  Card,
  Field,
  Row,
  Sel,
  StatusBadge,
  Text,
  useAsync,
} from "./components";

const TYPES = [
  { value: "SINGLE_MCQ", label: "Single choice" },
  { value: "MULTI_SELECT", label: "Multi-select" },
  { value: "TRUE_FALSE", label: "True / False" },
  { value: "SCENARIO_CHOICE", label: "Scenario-based choice" },
];

interface FormState {
  type: string;
  stem: string;
  optionTexts: string[];
  correct: Set<number>;
  explanation: string;
  category: string;
  difficulty: string;
}

function emptyForm(): FormState {
  return {
    type: "SINGLE_MCQ",
    stem: "",
    optionTexts: ["", ""],
    correct: new Set(),
    explanation: "",
    category: "",
    difficulty: "",
  };
}

function formFromQuestion(q: api.QuestionRow): FormState {
  const idToIndex = new Map(q.options.map((o, i) => [o.id, i]));
  const correct = new Set(
    q.correctOptionIds.map((id) => idToIndex.get(id)).filter((i): i is number => i !== undefined),
  );
  return {
    type: q.type,
    stem: q.stem,
    optionTexts: q.options.map((o) => o.text),
    correct,
    explanation: q.explanation ?? "",
    category: q.category ?? "",
    difficulty: q.difficulty ?? "",
  };
}

function QuestionForm({
  bankId,
  editing,
  onSaved,
  onCancel,
}: {
  bankId: string;
  editing: api.QuestionRow | null;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<FormState>(editing ? formFromQuestion(editing) : emptyForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(editing ? formFromQuestion(editing) : emptyForm());
  }, [editing]);

  function setType(type: string) {
    if (type === "TRUE_FALSE") {
      setForm((f) => ({ ...f, type, optionTexts: ["True", "False"], correct: new Set() }));
    } else {
      setForm((f) => ({
        ...f,
        type,
        optionTexts: f.optionTexts.length >= 2 ? f.optionTexts : ["", ""],
      }));
    }
  }

  function setOptionText(i: number, text: string) {
    setForm((f) => ({ ...f, optionTexts: f.optionTexts.map((t, idx) => (idx === i ? text : t)) }));
  }
  function addOption() {
    setForm((f) => ({ ...f, optionTexts: [...f.optionTexts, ""] }));
  }
  function removeOption(i: number) {
    setForm((f) => ({
      ...f,
      optionTexts: f.optionTexts.filter((_, idx) => idx !== i),
      correct: new Set([...f.correct].filter((c) => c !== i).map((c) => (c > i ? c - 1 : c))),
    }));
  }
  function toggleCorrect(i: number) {
    const multi = form.type === "MULTI_SELECT";
    setForm((f) => {
      const next = new Set(multi ? f.correct : []);
      if (multi && f.correct.has(i)) next.delete(i);
      else next.add(i);
      return { ...f, correct: next };
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const options = form.optionTexts.map((text, i) => ({ id: `opt-${i}`, text }));
      const correctOptionIds = [...form.correct].sort((a, b) => a - b).map((i) => `opt-${i}`);
      const payload = {
        type: form.type,
        stem: form.stem,
        options,
        correctOptionIds,
        explanation: form.explanation || undefined,
        category: form.category || undefined,
        difficulty: form.difficulty || undefined,
      };
      if (editing) {
        await api.updateQuestion(editing.id, payload);
      } else {
        await api.createQuestion({ bankId, ...payload });
      }
      onSaved();
      if (!editing) setForm(emptyForm());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const multi = form.type === "MULTI_SELECT";
  const canRemoveOptions = form.type !== "TRUE_FALSE";
  const valid =
    form.stem.trim().length > 0 &&
    form.optionTexts.every((t) => t.trim().length > 0) &&
    form.correct.size > 0;

  return (
    <div>
      <Field label="Type">
        <Sel value={form.type} onChange={(e) => setType(String(e.target.value))}>
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Sel>
      </Field>
      <Field label="Question / scenario">
        <Area
          value={form.stem}
          onChange={(e) => setForm((f) => ({ ...f, stem: e.target.value }))}
          rows={3}
        />
      </Field>
      <Field
        label={
          multi ? "Options (check all correct answers)" : "Options (select the correct answer)"
        }
      >
        <div>
          {form.optionTexts.map((text, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: "0.5rem",
                alignItems: "center",
                marginBottom: "0.4rem",
              }}
            >
              <input
                type={multi ? "checkbox" : "radio"}
                name="correct"
                checked={form.correct.has(i)}
                onChange={() => toggleCorrect(i)}
              />
              <Text
                value={text}
                onChange={(e) => setOptionText(i, e.target.value)}
                disabled={form.type === "TRUE_FALSE"}
                style={{ flex: 1 }}
              />
              {canRemoveOptions && form.optionTexts.length > 2 && (
                <Btn kind="ghost" onClick={() => removeOption(i)}>
                  Remove
                </Btn>
              )}
            </div>
          ))}
          {canRemoveOptions && (
            <Btn kind="ghost" onClick={addOption}>
              + Add option
            </Btn>
          )}
        </div>
      </Field>
      <Field label="Explanation (shown after submission)">
        <Area
          value={form.explanation}
          onChange={(e) => setForm((f) => ({ ...f, explanation: e.target.value }))}
          rows={2}
        />
      </Field>
      <div style={{ display: "flex", gap: "0.75rem" }}>
        <Field label="Category (optional)">
          <Text
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          />
        </Field>
        <Field label="Difficulty (optional)">
          <Text
            value={form.difficulty}
            onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value }))}
          />
        </Field>
      </div>
      {error && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{error}</p>}
      <div style={{ display: "flex", gap: "0.6rem" }}>
        <Btn onClick={save} disabled={busy || !valid}>
          {busy ? "Saving…" : editing ? "Save question" : "Add question"}
        </Btn>
        {onCancel && (
          <Btn kind="ghost" onClick={onCancel}>
            Cancel
          </Btn>
        )}
      </div>
    </div>
  );
}

export function QuestionBankPage() {
  const { id = "" } = useParams();
  const { data: bank, error, reload } = useAsync(() => api.getQuestionBank(id), [id]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const importFileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<api.ImportQuestionsResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (error)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Question bank">
        {error}
      </AdminShell>
    );
  if (!bank)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Loading…">
        {null}
      </AdminShell>
    );

  const editing = bank.questions.find((q) => q.id === editingId) ?? null;

  async function runImport() {
    const file = importFileRef.current?.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const result = await api.importQuestions(bank!.id, file);
      setImportResult(result);
      if (importFileRef.current) importFileRef.current.value = "";
      reload();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : String(e));
    } finally {
      setImporting(false);
    }
  }

  function toggleSelected(qid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === bank!.questions.length ? new Set() : new Set(bank!.questions.map((q) => q.id)),
    );
  }

  async function togglePublished(qid: string, publish: boolean) {
    setBusyId(qid);
    setActionError(null);
    try {
      await api.setQuestionPublished(qid, publish);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function bulkSetPublished(publish: boolean) {
    if (selected.size === 0) return;
    setBulkBusy(true);
    setActionError(null);
    try {
      await api.setQuestionsPublished([...selected], publish);
      setSelected(new Set());
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <AdminShell
      crumbs={[
        { label: "Admin", to: "/admin" },
        ...(bank.trackId ? [{ label: "Track", to: `/admin/tracks/${bank.trackId}` }] : []),
        { label: bank.title },
      ]}
      title={bank.title}
    >
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.5rem",
          }}
        >
          <strong>Questions ({bank.questions.length})</strong>
          {bank.questions.length > 0 && (
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <span style={{ fontSize: "0.85rem", color: "#666" }}>
                {selected.size > 0 ? `${selected.size} selected` : "Select questions to publish"}
              </span>
              <Btn
                kind="ghost"
                disabled={selected.size === 0 || bulkBusy}
                onClick={() => bulkSetPublished(true)}
              >
                {bulkBusy ? "Working…" : `Publish selected (${selected.size})`}
              </Btn>
              <Btn
                kind="ghost"
                disabled={selected.size === 0 || bulkBusy}
                onClick={() => bulkSetPublished(false)}
              >
                {bulkBusy ? "Working…" : `Unpublish selected (${selected.size})`}
              </Btn>
            </div>
          )}
        </div>
        {actionError && (
          <p style={{ color: "#b00020", fontSize: "0.85rem", marginTop: "0.4rem" }}>
            {actionError}
          </p>
        )}
        {bank.questions.length === 0 && <p style={{ color: "#666" }}>No questions yet.</p>}
        {bank.questions.length > 0 && (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: "0.8rem",
              color: "#666",
              padding: "0.3rem 0",
            }}
          >
            <input
              type="checkbox"
              checked={selected.size === bank.questions.length}
              onChange={toggleSelectAll}
            />
            Select all
          </label>
        )}
        {bank.questions.map((q) => (
          <Row key={q.id}>
            <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={selected.has(q.id)}
                onChange={() => toggleSelected(q.id)}
              />
              <span
                onClick={() => setEditingId(q.id)}
                style={{ cursor: "pointer", color: "#1a1a2e" }}
                title="Click to edit"
              >
                {q.stem.length > 80 ? `${q.stem.slice(0, 80)}…` : q.stem}
                <span style={{ color: "#888" }}> · {q.type.replace(/_/g, " ").toLowerCase()}</span>
              </span>
            </span>
            <span style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <StatusBadge status={q.status} />
              <Btn
                kind="ghost"
                disabled={busyId === q.id}
                onClick={() => togglePublished(q.id, q.status !== "PUBLISHED")}
              >
                {busyId === q.id ? "Working…" : q.status === "PUBLISHED" ? "Unpublish" : "Publish"}
              </Btn>
            </span>
          </Row>
        ))}
      </Card>

      <Card>
        <strong>Import questions</strong>
        <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.2rem" }}>
          Upload a <code>.xlsx</code>/<code>.xls</code>, <code>.txt</code>, or <code>.pdf</code>{" "}
          file. Imported questions are added as drafts — check them over below, then select the ones
          you want (or "Select all") and publish them in one go.
        </p>
        <details style={{ fontSize: "0.82rem", color: "#555", marginBottom: "0.6rem" }}>
          <summary style={{ cursor: "pointer" }}>Expected format</summary>
          <p style={{ marginBottom: "0.3rem" }}>
            <strong>.xlsx / .xls</strong> — one row per question, columns: Type, Question, Option
            A..Option F, Correct (letter(s), e.g. "B" or "A,C"), Explanation, Category, Difficulty.
            Type is optional (SINGLE_MCQ, MULTI_SELECT, TRUE_FALSE, or SCENARIO_CHOICE) — it's
            inferred as MULTI_SELECT when more than one letter is marked correct, otherwise
            SINGLE_MCQ.
          </p>
          <p style={{ margin: 0 }}>
            <strong>.txt / .pdf</strong> — one question per paragraph (blank line, or a line of{" "}
            <code>---</code>, between questions):
          </p>
          <pre
            style={{
              background: "#f7f7fa",
              padding: "0.5rem",
              borderRadius: 6,
              fontSize: "0.78rem",
              overflowX: "auto",
            }}
          >
            {`Q1. What is the capital of France?
Type: SINGLE_MCQ
A) London
B) Paris *
C) Berlin
D) Madrid
Explanation: Paris has been the capital since 987 AD.
Category: Geography
Difficulty: Easy`}
          </pre>
          <p style={{ margin: 0 }}>
            Mark correct option(s) with a trailing <code>*</code>. Type/Explanation/Category/
            Difficulty lines are all optional. A PDF must contain text in this same layout — a
            scanned image or a free-form table won't parse reliably.
          </p>
        </details>
        <input ref={importFileRef} type="file" accept=".xlsx,.xls,.txt,.pdf" />
        <div style={{ marginTop: "0.6rem" }}>
          <Btn onClick={runImport} disabled={importing}>
            {importing ? "Importing…" : "Import"}
          </Btn>
        </div>
        {importResult && (
          <div style={{ marginTop: "0.6rem" }}>
            <p style={{ color: "#0a7d33", fontSize: "0.85rem", margin: 0 }}>
              Imported {importResult.imported} question(s).
            </p>
            {importResult.skipped.length > 0 && (
              <div style={{ marginTop: "0.3rem" }}>
                <p style={{ color: "#b00020", fontSize: "0.85rem", margin: 0 }}>
                  Skipped {importResult.skipped.length}:
                </p>
                <ul style={{ margin: "0.2rem 0 0", paddingLeft: "1.2rem" }}>
                  {importResult.skipped.map((s) => (
                    <li key={s.row} style={{ color: "#b00020", fontSize: "0.8rem" }}>
                      Row {s.row}: {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {importError && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{importError}</p>}
      </Card>

      <Card>
        <strong>{editing ? `Edit question` : "Add a question"}</strong>
        <div style={{ marginTop: "0.75rem" }}>
          <QuestionForm
            bankId={bank.id}
            editing={editing}
            onSaved={() => {
              setEditingId(null);
              reload();
            }}
            onCancel={editing ? () => setEditingId(null) : undefined}
          />
        </div>
      </Card>
    </AdminShell>
  );
}
